/**
 * Spaced Repetition Algorithm for Glissando
 * 
 * Weighted random selection that prioritizes:
 * 1. Notes the user struggles with (higher frequency)
 * 2. Notes not seen recently
 * 3. Mastered notes still appear at 20% floor rate
 */

// Select next note based on weighted probability
// excludeNote parameter ensures we never repeat the same note consecutively
export function selectNextNote(notesStats, unlockedNotes, recentNotes = [], excludeNote = null) {
  if (!unlockedNotes || unlockedNotes.length === 0) {
    return null;
  }
  
  // Filter out the note to exclude (the one just answered)
  let availableNotes = unlockedNotes;
  if (excludeNote && unlockedNotes.length > 1) {
    availableNotes = unlockedNotes.filter(note => note !== excludeNote);
  }
  
  // If somehow we filtered everything, fall back to all notes
  if (availableNotes.length === 0) {
    availableNotes = unlockedNotes;
  }
  
  // Calculate weights for each available note
  const weights = availableNotes.map(note => {
    const stats = notesStats[note];
    if (!stats) {
      return { note, weight: 1 }; // New notes get standard weight
    }
    
    let weight = stats.currentFrequency || 1;
    
    // Boost weight for notes not seen recently
    if (stats.lastShownTimestamp) {
      const timeSinceSeen = Date.now() - stats.lastShownTimestamp;
      const minutesSinceSeen = timeSinceSeen / (1000 * 60);
      
      // Increase weight for notes not seen in last 5 minutes
      if (minutesSinceSeen > 5) {
        weight *= 1.2;
      }
    } else {
      // Never seen notes get a boost
      weight *= 1.5;
    }
    
    // Reduce weight if note was recently shown (but not excluded)
    if (recentNotes.includes(note)) {
      const recencyIndex = recentNotes.indexOf(note);
      weight *= (0.4 + (recencyIndex * 0.15)); // More recent = lower weight
    }
    
    return { note, weight };
  });
  
  // Calculate total weight
  const totalWeight = weights.reduce((sum, w) => sum + w.weight, 0);
  
  // Random selection based on weights
  let random = Math.random() * totalWeight;
  
  for (const { note, weight } of weights) {
    random -= weight;
    if (random <= 0) {
      return note;
    }
  }
  
  // Fallback to random selection
  return unlockedNotes[Math.floor(Math.random() * unlockedNotes.length)];
}

// Select warm-up notes (previously seen notes for refresh)
export function selectWarmupNotes(notesStats, unlockedNotes, count = 5) {
  // Filter to notes that have been seen before
  const seenNotes = unlockedNotes.filter(note => {
    const stats = notesStats[note];
    return stats && (stats.correctCount > 0 || stats.incorrectCount > 0);
  });
  
  if (seenNotes.length === 0) {
    // No previous history - return random notes from unlocked set
    return shuffleArray([...unlockedNotes]).slice(0, count);
  }
  
  // Sort by when last seen (oldest first) and take the requested count
  const sortedByRecency = seenNotes.sort((a, b) => {
    const aTime = notesStats[a]?.lastShownTimestamp || 0;
    const bTime = notesStats[b]?.lastShownTimestamp || 0;
    return aTime - bTime;
  });
  
  // Take notes not seen recently + some random ones
  const warmupSet = new Set();
  
  // Add oldest seen notes
  for (let i = 0; i < Math.min(3, sortedByRecency.length); i++) {
    warmupSet.add(sortedByRecency[i]);
  }
  
  // Fill remaining with weighted random selection
  while (warmupSet.size < count && warmupSet.size < unlockedNotes.length) {
    const note = selectNextNote(notesStats, unlockedNotes, [...warmupSet]);
    if (note) warmupSet.add(note);
  }
  
  return shuffleArray([...warmupSet]);
}

// Generate a full session of notes
export function generateSessionNotes(notesStats, unlockedNotes, sessionLength = 25, includeWarmup = true) {
  const sessionNotes = [];
  const recentBuffer = []; // Track recent notes to avoid immediate repeats
  const recentBufferSize = 3;
  
  // Add warmup notes if requested
  if (includeWarmup) {
    const warmupNotes = selectWarmupNotes(notesStats, unlockedNotes, Math.min(5, Math.floor(sessionLength * 0.2)));
    sessionNotes.push(...warmupNotes);
    recentBuffer.push(...warmupNotes.slice(-recentBufferSize));
  }
  
  // Generate remaining notes
  while (sessionNotes.length < sessionLength) {
    const nextNote = selectNextNote(notesStats, unlockedNotes, recentBuffer);
    if (nextNote) {
      sessionNotes.push(nextNote);
      recentBuffer.push(nextNote);
      if (recentBuffer.length > recentBufferSize) {
        recentBuffer.shift();
      }
    }
  }
  
  return sessionNotes;
}

// Helper: Fisher-Yates shuffle
function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Calculate session metrics
export function calculateSessionMetrics(sessionResults) {
  if (!sessionResults || sessionResults.length === 0) {
    return {
      accuracy: 0,
      avgResponseTime: 0,
      correctCount: 0,
      incorrectCount: 0,
      notesMastered: [],
      challengingNotes: [],
      missedNotes: [],
      noteBreakdown: {}
    };
  }
  
  const correctCount = sessionResults.filter(r => r.isCorrect).length;
  const incorrectCount = sessionResults.length - correctCount;
  const accuracy = (correctCount / sessionResults.length) * 100;
  
  const responseTimes = sessionResults.map(r => r.responseTime).filter(t => t > 0);
  const avgResponseTime = responseTimes.length > 0 
    ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
    : 0;
  
  // Group by note and calculate per-note stats
  const noteResults = {};
  sessionResults.forEach(r => {
    if (!noteResults[r.note]) {
      noteResults[r.note] = { correct: 0, incorrect: 0, responseTimes: [] };
    }
    if (r.isCorrect) {
      noteResults[r.note].correct++;
    } else {
      noteResults[r.note].incorrect++;
    }
    noteResults[r.note].responseTimes.push(r.responseTime);
  });
  
  // Create note breakdown with accuracy per note
  const noteBreakdown = Object.entries(noteResults).map(([note, stats]) => {
    const total = stats.correct + stats.incorrect;
    const noteAccuracy = total > 0 ? Math.round((stats.correct / total) * 100) : 0;
    const avgTime = stats.responseTimes.length > 0 
      ? Math.round(stats.responseTimes.reduce((a, b) => a + b, 0) / stats.responseTimes.length)
      : 0;
    return { note, correct: stats.correct, incorrect: stats.incorrect, accuracy: noteAccuracy, avgTime };
  });
  
  // Notes that were missed (any incorrect answer)
  const missedNotes = noteBreakdown
    .filter(n => n.incorrect > 0)
    .sort((a, b) => b.incorrect - a.incorrect);
  
  // Identify challenging notes (more than 50% incorrect in session)
  const challengingNotes = noteBreakdown
    .filter(n => {
      const total = n.correct + n.incorrect;
      return total >= 2 && n.incorrect / total > 0.5;
    })
    .map(n => n.note);
  
  return {
    accuracy: Math.round(accuracy * 10) / 10,
    avgResponseTime: Math.round(avgResponseTime),
    correctCount,
    incorrectCount,
    totalQuestions: sessionResults.length,
    challengingNotes,
    missedNotes,
    noteBreakdown
  };
}

export default {
  selectNextNote,
  selectWarmupNotes,
  generateSessionNotes,
  calculateSessionMetrics
};
